/**
 * Create a lead.
 *
 * The action validates with `createLeadInput` — the same schema the server uses
 * — so a blank form reports "Provide at least a name, organization, or email"
 * inline instead of round-tripping to a 400. The server still validates; this is
 * for the person filling the form.
 */

import { Form, redirect, useActionData, useNavigation } from 'react-router-dom'
import type { ActionFunctionArgs } from 'react-router-dom'
import { createLeadInput } from '@erp/contracts'
import { api, ApiError } from '../api'
import { invalid, issuesFrom, type FormResult } from '../forms'
import { LeadFields, readLeadForm } from '../components/lead-form'
import { Button, PageHeader } from '../ui'

export async function action({ request }: ActionFunctionArgs): Promise<FormResult | Response> {
  const parsed = createLeadInput.safeParse(readLeadForm(await request.formData()))
  if (!parsed.success) return invalid(issuesFrom(parsed.error))

  try {
    const lead = await api.leads.create(parsed.data)
    return redirect(`/crm/leads/${lead.id}`)
  } catch (error) {
    if (error instanceof ApiError) return invalid(error.issues, error.message)
    throw error
  }
}

export default function NewLead() {
  const result = useActionData() as FormResult | undefined
  const navigation = useNavigation()
  const saving = navigation.state === 'submitting'

  return (
    <>
      <PageHeader title="New lead" />
      {result?.message ? <div className="ax-alert ax-alert-danger">{result.message}</div> : null}

      <Form method="post" className="ax-form">
        <LeadFields issues={result?.issues ?? {}} />
        <div className="ax-form-actions">
          <Button variant="solid" type="submit" disabled={saving}>
            {saving ? 'Creating…' : 'Create lead'}
          </Button>
          <a className="ax-btn" href="/crm/leads">
            Cancel
          </a>
        </div>
      </Form>
    </>
  )
}
